<script lang="ts">
  import LegalDocument from '../components/LegalDocument.svelte'
</script>

<LegalDocument
  title="Terms of Service"
  description="These terms describe the ground rules for using Reading Now."
  lastUpdated="September 14, 2026"
>
  <section>
    <h2>Agreement to these terms</h2>
    <p>
      By accessing or using Reading Now, you agree to these Terms of Service and
      the Privacy Policy. If you do not agree, do not use the service. Reading
      Now is an independent service operated by its maintainer.
    </p>
  </section>

  <section>
    <h2>Eligibility and accounts</h2>
    <p>
      You must be at least 13 years old to use Reading Now. If the law where you
      live requires a greater age for you to consent to online services, you
      must meet that requirement or have permission from a parent or guardian.
    </p>
    <p>
      You are responsible for providing accurate account information, protecting
      your password and connected sign-in methods, and for activity under your
      account. Tell us promptly if you believe your account has been
      compromised.
    </p>
  </section>

  <section>
    <h2>Using Reading Now</h2>
    <p>
      You may use the service for lawful personal and community reading
      activity. You may not:
    </p>
    <ul>
      <li>Harass, threaten, impersonate, or harm another person.</li>
      <li>Post unlawful, infringing, deceptive, or malicious content.</li>
      <li>Attempt to access another account or bypass security controls.</li>
      <li>
        Scrape, overload, disrupt, reverse engineer, or abuse the service.
      </li>
      <li>Use automated access unless we have expressly permitted it.</li>
    </ul>
    <p>
      We may remove content or restrict activity that violates these terms or
      creates risk for users or the service.
    </p>
  </section>

  <section>
    <h2>Your content</h2>
    <p>
      You keep ownership of content you create. You give Reading Now a
      non-exclusive, worldwide, royalty-free license to host, store, reproduce,
      display, and distribute that content only as needed to operate, secure,
      and improve the service and to show it according to your visibility
      choices. This license ends when the content is deleted, except where a
      limited copy must remain in backups, logs, or content shared with others.
    </p>
    <p>
      You represent that you have the rights needed to submit your content and
      that its use through Reading Now will not violate another person's rights.
    </p>
  </section>

  <section>
    <h2>Privacy</h2>
    <p>
      Our <a href="/privacy">Privacy Policy</a> explains how we collect, use, and
      share information. By using Reading Now, you acknowledge those data practices.
    </p>
  </section>

  <section>
    <h2>Third-party services and content</h2>
    <p>
      Reading Now relies on third-party providers and may display book metadata,
      cover images, links, or other material supplied by third parties. We do
      not control those services or guarantee that third-party information is
      complete, accurate, or continuously available. Your use of a third-party
      service may also be governed by that provider's terms and policies.
    </p>
  </section>

  <section>
    <h2>Service changes and availability</h2>
    <p>
      We may modify, suspend, or discontinue features, and we may introduce
      limits needed to keep Reading Now safe and reliable. We aim to keep the
      service available, but we do not guarantee uninterrupted or error-free
      operation or permanent storage of any content. Keep your own copy of
      anything you cannot afford to lose.
    </p>
  </section>

  <section>
    <h2>Suspension and termination</h2>
    <p>
      You may stop using Reading Now at any time. You can request account
      deletion by emailing
      <a href="mailto:ayellapragada@gmail.com">ayellapragada@gmail.com</a>. We
      may suspend or terminate access when reasonably necessary to enforce these
      terms, protect users, comply with law, or protect the service.
    </p>
  </section>

  <section>
    <h2>Disclaimers</h2>
    <p>
      To the fullest extent permitted by law, Reading Now is provided “as is”
      and “as available,” without warranties of any kind, whether express or
      implied. Reading recommendations and other content are for general
      informational and entertainment purposes.
    </p>
  </section>

  <section>
    <h2>Limitation of liability</h2>
    <p>
      To the fullest extent permitted by law, Reading Now and its maintainer
      will not be liable for indirect, incidental, special, consequential, or
      punitive damages, or for lost data, use, goodwill, or profits arising from
      your use of or inability to use the service. Some jurisdictions do not
      allow certain limitations, so parts of this section may not apply to you.
    </p>
  </section>

  <section>
    <h2>Changes and contact</h2>
    <p>
      We may update these terms as the service changes. We will update the date
      above and provide additional notice when appropriate. Continued use after
      an update means you accept the revised terms. Questions can be sent to
      <a href="mailto:ayellapragada@gmail.com">ayellapragada@gmail.com</a>.
    </p>
  </section>
</LegalDocument>
